import { DotsThreeVerticalIcon } from "@phosphor-icons/react/dist/csr/DotsThreeVertical";
import { EnvelopeSimpleIcon } from "@phosphor-icons/react/dist/csr/EnvelopeSimple";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { isProductEnabled } from "@/business/product-access";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useDeleteUser } from "@/business-settings/users/context/delete-user-context";
import { getAccessSummary } from "@/business-settings/users/workbook-access/access-summary";
import { useUsersWorkbookAccess } from "@/business-settings/users/workbook-access/use-users-workbook-access";
import { useAuth } from "@/core/auth";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { getUserFullName } from "@/core/utils/name";
import { WorkbookAccessLink } from "@/payroll/workbook-access/workbook-access-link";
import { USER_ROLES } from "@/user/constants";
import { useUserManagementAccess } from "@/user/management-access";
import { useGetBusinessUser, useResendWelcomeEmail } from "@/user/query";
import { UserAccountForm } from "@/user/user-account-form";
import type { UserAccountValues } from "@/user/user-account-form/types";
import { WelcomeEmailStatus } from "@/user/welcome-email-notice";
import { UserReferralDetails } from "./user-referral-details";

interface WebAppUserSidebarProps {
  businessId: string;
  mode: "view" | "new";
  userId: string | undefined;
}

const WebAppUserSidebar = ({
  businessId,
  mode,
  userId,
}: WebAppUserSidebarProps) => {
  const {
    data: user,
    isLoading,
    isError,
  } = useGetBusinessUser(businessId, userId);
  const { canManageUsers, canManageWorkbookAccess } =
    useUserManagementAccess(businessId);
  const {
    updateSidebar,
    resetSidebar,
    accountDrafts: drafts,
  } = useBusinessSettingsSidebar();
  const { user: viewer } = useAuth();
  const { setDeleteUserId } = useDeleteUser();
  const { settings } = useBusinessProducts();
  const [showReferrals, setShowReferrals] = useState(false);
  const [failedEmailUserId, setFailedEmailUserId] = useState<string>();
  const sendWelcomeEmail = useResendWelcomeEmail(userId ?? "");
  const [emailResult, setEmailResult] = useState<{
    userId: string;
    sent: boolean;
  }>();
  const currentEmailResult =
    emailResult?.userId === userId ? emailResult : undefined;
  const draftKey = `${viewer?.id}:${businessId}:${userId ?? "new"}`;
  const activeDraft = useRef(draftKey);
  useLayoutEffect(() => {
    activeDraft.current = draftKey;
  }, [draftKey]);
  const storeDraft = useCallback(
    (values: UserAccountValues | undefined) => {
      if (values) drafts.current.set(draftKey, values);
      else drafts.current.delete(draftKey);
    },
    [draftKey, drafts],
  );
  const cancel = () => {
    drafts.current.delete(draftKey);
    resetSidebar();
  };
  const enableReferrals = isProductEnabled(settings, "referrals");
  const workbookAccess = useUsersWorkbookAccess(businessId);
  const accessPerson = workbookAccess.settings?.users.find(
    (person) => person.id === userId,
  );
  if (!canManageUsers && !canManageWorkbookAccess) return null;
  const accessSummary =
    workbookAccess.settings && userId
      ? getAccessSummary(workbookAccess.settings, userId)
      : undefined;
  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        className="items-center"
        headerText="Users"
        name={
          user
            ? getUserFullName(user) || user.email
            : (accessPerson?.name ?? "New user")
        }
        onClose={() => updateSidebar({ isOpen: false })}
        actions={
          canManageUsers &&
          mode === "view" &&
          user &&
          user.role !== USER_ROLES.TECH && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="More account actions"
                    className="ml-auto shrink-0"
                  />
                }
              >
                <DotsThreeVerticalIcon />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-56">
                <DropdownMenuItem
                  disabled={sendWelcomeEmail.isPending}
                  onClick={() =>
                    sendWelcomeEmail.mutate(undefined, {
                      onSuccess: () =>
                        setEmailResult({ userId: user.id, sent: true }),
                      onError: () =>
                        setEmailResult({ userId: user.id, sent: false }),
                    })
                  }
                >
                  <EnvelopeSimpleIcon />
                  {sendWelcomeEmail.isPending
                    ? "Sending welcome email…"
                    : "Resend welcome email"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => setDeleteUserId(user.id)}
                >
                  <TrashIcon />
                  Delete user
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      />
      <Stack gap={4} className="min-h-0 w-full flex-1 overflow-auto p-6">
        <WelcomeEmailStatus
          sent={currentEmailResult?.sent}
          failed={
            currentEmailResult?.sent === false ||
            (!!userId && failedEmailUserId === userId)
          }
        />
        {isLoading ? (
          <Spinner layout="centered" aria-label="Loading" />
        ) : (isError || (userId && !user)) && !accessPerson ? (
          <Alert color="danger">
            Could not load this account. Close the panel and try again.
          </Alert>
        ) : canManageUsers && (!userId || user) ? (
          <UserAccountForm
            key={draftKey}
            businessId={businessId}
            user={user}
            allowTechnicianConversion
            draft={drafts.current.get(draftKey)}
            onDraftChange={storeDraft}
            onSaved={(saved, failed) => {
              if (failed) setFailedEmailUserId(saved.id);
              if (activeDraft.current !== draftKey) return;
              if (saved.role === USER_ROLES.TECH) {
                resetSidebar();
                return;
              }
              updateSidebar({
                mode: SIDEBAR_MODES.view,
                selectedItemId: saved.id,
              });
            }}
            onCancel={cancel}
          />
        ) : accessPerson ? (
          <Stack gap={2} className="w-full">
            <Text>{accessPerson.email}</Text>
            <Text tone="muted">
              {accessPerson.removed
                ? "This user was removed. You can remove their remaining workbook access."
                : "This account is managed outside this business."}
            </Text>
          </Stack>
        ) : null}
        {userId && workbookAccess.settings && accessPerson && (
          <Stack gap={2} className="w-full border-t pt-4">
            <Text variant="title">Workbook access</Text>
            <Text tone="muted">
              {accessSummary?.scope}
              {!accessSummary?.empty && ` · ${accessSummary?.rights}`}
            </Text>
            <Stack align="start">
              <WorkbookAccessLink userId={userId}>
                Manage workbook access
              </WorkbookAccessLink>
            </Stack>
          </Stack>
        )}
        {userId && workbookAccess.query.isError && (
          <Alert color="danger">
            Could not load workbook access.{" "}
            <Button
              variant="link"
              onClick={() => void workbookAccess.query.refetch()}
            >
              Try again
            </Button>
          </Alert>
        )}
        {enableReferrals && user && (
          <UserReferralDetails
            user={user}
            open={showReferrals}
            onOpenChange={setShowReferrals}
          />
        )}
      </Stack>
    </BusinessSettingsSidebarWrapper>
  );
};

export default WebAppUserSidebar;
